/* ==========================================================================
   Bolo theme — single deferred bundle. Vanilla JS, no dependencies.
   Modules: utils · reveal · header · announcement · drawers/modals · search
   cart · quick-add · product form · gallery · sticky ATC · collection filters
   accordions · popup · newsletter · recently viewed · misc
   ========================================================================== */
(function () {
  'use strict';

  var B = window.Bolo || {};
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var $ = function (sel, ctx) { return (ctx || document).querySelector(sel); };
  var $$ = function (sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); };

  /* ---------- utils -------------------------------------------------------- */
  function formatMoney(cents, format) {
    format = format || B.moneyFormat || '£{{amount}}';
    var value = '';
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;
    var match = format.match(placeholder);
    var type = match ? match[1] : 'amount';
    function fmt(number, precision, thousands, decimal) {
      number = (number / 100).toFixed(precision);
      var parts = number.split('.');
      var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return whole + (parts[1] ? decimal + parts[1] : '');
    }
    switch (type) {
      case 'amount': value = fmt(cents, 2, ',', '.'); break;
      case 'amount_no_decimals': value = fmt(cents, 0, ',', '.'); break;
      case 'amount_with_comma_separator': value = fmt(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = fmt(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = fmt(cents, 2, "'", '.'); break;
      default: value = fmt(cents, 2, ',', '.');
    }
    return format.replace(placeholder, value);
  }

  function debounce(fn, wait) { var t; return function () { var a = arguments, c = this; clearTimeout(t); t = setTimeout(function () { fn.apply(c, a); }, wait); }; }
  function throttle(fn, wait) { var last = 0, t; return function () { var now = Date.now(), a = arguments, c = this; if (now - last >= wait) { last = now; fn.apply(c, a); } else { clearTimeout(t); t = setTimeout(function () { last = Date.now(); fn.apply(c, a); }, wait - (now - last)); } }; }

  var storage = {
    get: function (k) { try { return JSON.parse(localStorage.getItem(k)); } catch (e) { return null; } },
    set: function (k, v) { try { localStorage.setItem(k, JSON.stringify(v)); } catch (e) { /* private mode */ } },
    remove: function (k) { try { localStorage.removeItem(k); } catch (e) {} }
  };

  var lockCount = 0;
  function lockScroll() { lockCount++; document.body.classList.add('is-locked'); }
  function unlockScroll() { lockCount = Math.max(0, lockCount - 1); if (!lockCount) document.body.classList.remove('is-locked'); }

  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type=hidden]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  function trapFocus(container) {
    var previous = document.activeElement;
    function getFocusable() { return $$(FOCUSABLE, container).filter(function (el) { return el.offsetParent !== null; }); }
    function onKey(e) {
      if (e.key !== 'Tab') return;
      var items = getFocusable();
      if (!items.length) { e.preventDefault(); return; }
      var first = items[0], last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
    container.addEventListener('keydown', onKey);
    var target = container.querySelector('[data-autofocus]') || getFocusable()[0];
    if (target) setTimeout(function () { target.focus(); }, 30);
    return function release() { container.removeEventListener('keydown', onKey); if (previous && previous.focus) previous.focus(); };
  }

  function toast(message, type) {
    var host = $('#BoloToasts'); if (!host) return;
    var el = document.createElement('div');
    el.className = 'bolo-toast' + (type ? ' bolo-toast--' + type : '');
    el.setAttribute('role', 'status');
    el.textContent = message;
    host.appendChild(el);
    setTimeout(function () { el.style.opacity = '0'; el.style.transition = 'opacity .3s'; setTimeout(function () { el.remove(); }, 320); }, 3200);
  }

  /* ---------- scroll reveal ------------------------------------------------ */
  function initReveal() {
    var els = $$('[data-reveal]');
    if (!els.length) return;
    if (reduceMotion || B.reveal === false || !('IntersectionObserver' in window)) {
      document.documentElement.classList.add('no-reveal'); return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (!en.isIntersecting) return;
        var el = en.target;
        var siblings = el.parentElement ? $$(':scope > [data-reveal]', el.parentElement) : [];
        var idx = Math.max(0, siblings.indexOf(el));
        el.style.setProperty('--reveal-delay', Math.min(idx, 6) * 70 + 'ms');
        el.classList.add('is-revealed');
        io.unobserve(el);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    els.forEach(function (el) { io.observe(el); });
  }

  /* ---------- header ------------------------------------------------------- */
  function initHeader() {
    var group = $('[data-header-group]');
    var header = $('[data-header]');
    if (!header) return;
    var setTotal = function () { if (group) document.documentElement.style.setProperty('--header-total', group.offsetHeight + 'px'); };
    setTotal(); window.addEventListener('resize', debounce(setTotal, 150)); document.addEventListener('bolo:announcement:dismissed', setTotal);
    var transparent = header.classList.contains('bolo-header--transparent');
    var hero = $('[data-hero]');
    if (transparent && !hero) { header.classList.remove('bolo-header--transparent'); transparent = false; }
    if (transparent && group) {
      // Header overlays the hero. Once we scroll past the hero's top edge + a small offset, go solid.
      var threshold = 40;
      var update = function () {
        var solid = window.scrollY > threshold;
        header.classList.toggle('is-solid', solid);
      };
      window.addEventListener('scroll', throttle(update, 80), { passive: true });
      update();
      if (hero) {
        // Solid whenever the menu/search overlays are open (keeps contrast).
        document.addEventListener('bolo:overlay', function (e) { header.classList.toggle('is-forced', !!e.detail.open); if (e.detail.open) header.classList.add('is-solid'); else update(); });
      }
    } else {
      var onScroll = throttle(function () { header.classList.toggle('is-solid', window.scrollY > 4); }, 80);
      window.addEventListener('scroll', onScroll, { passive: true }); onScroll();
    }
  }

  /* ---------- announcement bar -------------------------------------------- */
  function initAnnouncement() {
    var bar = $('[data-announcement]'); if (!bar) return;
    var key = 'bolo:announcement:' + (bar.dataset.version || '1');
    if (storage.get(key)) { bar.hidden = true; document.dispatchEvent(new CustomEvent('bolo:announcement:dismissed')); return; }
    var items = $$('[data-announcement-item]', bar);
    var dismiss = $('[data-announcement-dismiss]', bar);
    if (dismiss) dismiss.addEventListener('click', function () { bar.hidden = true; storage.set(key, Date.now()); document.dispatchEvent(new CustomEvent('bolo:announcement:dismissed')); });
    if (items.length < 2 || reduceMotion) { if (items[0]) items[0].classList.add('is-active'); return; }
    var i = 0; items[0].classList.add('is-active');
    var interval = parseInt(bar.dataset.interval || '5000', 10);
    var timer = setInterval(function () {
      items[i].classList.remove('is-active'); i = (i + 1) % items.length; items[i].classList.add('is-active');
    }, interval);
    bar.addEventListener('mouseenter', function () { clearInterval(timer); });
  }

  /* ---------- generic drawer / modal controller ---------------------------- */
  var overlays = {};
  function registerOverlay(id, opts) {
    var root = document.getElementById(id); if (!root) return null;
    var release = null;
    var api = {
      root: root,
      isOpen: function () { return root.classList.contains('is-open'); },
      open: function () {
        if (api.isOpen()) return;
        root.hidden = false;
        // next frame so the transition runs
        requestAnimationFrame(function () { root.classList.add('is-open'); });
        lockScroll();
        release = trapFocus(root.querySelector('[role="dialog"]') || root);
        document.dispatchEvent(new CustomEvent('bolo:overlay', { detail: { id: id, open: true } }));
        if (opts && opts.onOpen) opts.onOpen();
      },
      close: function () {
        if (!api.isOpen()) return;
        root.classList.remove('is-open');
        unlockScroll();
        if (release) { release(); release = null; }
        var done = function () { if (!root.classList.contains('is-open')) root.hidden = true; };
        setTimeout(done, reduceMotion ? 0 : 380);
        document.dispatchEvent(new CustomEvent('bolo:overlay', { detail: { id: id, open: false } }));
        if (opts && opts.onClose) opts.onClose();
      },
      toggle: function () { api.isOpen() ? api.close() : api.open(); }
    };
    $$('[data-close="' + id + '"], [data-overlay-close]', root).forEach(function (btn) { btn.addEventListener('click', api.close); });
    overlays[id] = api;
    return api;
  }
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    Object.keys(overlays).forEach(function (k) { if (overlays[k].isOpen()) overlays[k].close(); });
    var qa = $('#BoloQuickAdd'); if (qa && !qa.hidden) closeQuickAdd();
    var zoom = $('.bolo-zoom'); if (zoom) zoom.remove();
  });
  document.addEventListener('click', function (e) {
    var t = e.target.closest('[data-open]'); if (!t) return;
    var api = overlays[t.dataset.open]; if (api) { e.preventDefault(); api.open(); }
  });

  /* ---------- menu drawer, search, cart drawer, size guide ----------------- */
  function initOverlays() {
    registerOverlay('BoloMenu');
    registerOverlay('BoloSearch', { onOpen: function () { var i = $('#BoloSearchInput'); if (i) setTimeout(function () { i.focus(); }, 60); } });
    registerOverlay('BoloCartDrawer');
    registerOverlay('BoloSizeGuide');
    // Sub-menu toggles in mobile drawer
    $$('[data-menu-toggle]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var target = document.getElementById(btn.getAttribute('aria-controls'));
        var open = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', String(!open));
        if (target) target.classList.toggle('is-open', !open);
      });
    });
  }

  /* ---------- predictive search ------------------------------------------- */
  function initPredictiveSearch() {
    var input = $('#BoloSearchInput'); var results = $('[data-search-results]');
    if (!input || !results) return;
    var run = debounce(function () {
      var q = input.value.trim();
      if (q.length < 2) { results.innerHTML = ''; return; }
      var url = B.routes.predictiveSearch + '?q=' + encodeURIComponent(q) + '&resources[type]=product,article,page&resources[limit]=6&resources[options][unavailable_products]=last&section_id=predictive-search';
      fetch(url).then(function (r) { return r.text(); }).then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var inner = doc.querySelector('[data-predictive-inner]');
        results.innerHTML = inner ? inner.innerHTML : '';
      }).catch(function () {});
    }, 220);
    input.addEventListener('input', run);
  }

  /* ---------- cart --------------------------------------------------------- */
  var Cart = {
    sections: ['cart-drawer'],
    get: function () { return fetch(B.routes.cart + '.js').then(function (r) { return r.json(); }); },
    add: function (body) {
      body.sections = Cart.sections.join(',');
      return fetch(B.routes.cartAdd + '.js', { method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify(body) })
        .then(function (r) { return r.json().then(function (j) { if (!r.ok) throw j; return j; }); });
    },
    change: function (line, quantity) {
      return fetch(B.routes.cartChange + '.js', { method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify({ line: line, quantity: quantity, sections: Cart.sections.join(',') }) })
        .then(function (r) { return r.json().then(function (j) { if (!r.ok) throw j; return j; }); });
    },
    render: function (sectionsHtml) {
      if (!sectionsHtml) return Cart.refresh();
      var html = sectionsHtml['cart-drawer'];
      if (!html) return;
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var fresh = doc.querySelector('[data-cart-drawer-inner]');
      var current = $('[data-cart-drawer-inner]');
      if (fresh && current) { current.innerHTML = fresh.innerHTML; Cart.bind(current); }
      var count = doc.querySelector('[data-cart-count-source]');
      if (count) Cart.setCount(count.dataset.cartCountSource);
    },
    refresh: function () {
      fetch(window.location.pathname + '?sections=cart-drawer').then(function (r) { return r.json(); }).then(Cart.render).catch(function () {});
    },
    setCount: function (n) {
      $$('[data-cart-count]').forEach(function (el) { el.textContent = n; el.dataset.count = n; });
    },
    bind: function (root) {
      root = root || document;
      $$('[data-cart-qty]', root).forEach(function (wrap) {
        var input = $('input', wrap); var line = parseInt(wrap.dataset.line, 10);
        $$('[data-qty-minus],[data-qty-plus]', wrap).forEach(function (btn) {
          btn.addEventListener('click', function () {
            var v = parseInt(input.value, 10) || 0; v += btn.hasAttribute('data-qty-plus') ? 1 : -1;
            Cart.updateLine(line, Math.max(0, v), wrap.closest('[data-cart-item]'));
          });
        });
        input.addEventListener('change', function () { Cart.updateLine(line, Math.max(0, parseInt(input.value, 10) || 0), wrap.closest('[data-cart-item]')); });
      });
      $$('[data-cart-remove]', root).forEach(function (btn) {
        btn.addEventListener('click', function () { Cart.updateLine(parseInt(btn.dataset.line, 10), 0, btn.closest('[data-cart-item]')); });
      });
      $$('[data-quick-add-form]', root).forEach(bindQuickAddForm);
      $$('[data-quick-add-open]', root).forEach(bindQuickAddOpen);
    },
    updateLine: function (line, qty, item) {
      if (item) item.classList.add('is-updating');
      Cart.change(line, qty).then(function (cart) {
        Cart.render(cart.sections);
        document.dispatchEvent(new CustomEvent('bolo:cart:updated', { detail: cart }));
      }).catch(function (err) {
        if (item) item.classList.remove('is-updating');
        toast((err && err.description) || B.strings.error, 'error');
      });
    },
    added: function (result) {
      Cart.render(result.sections);
      var title = result.product_title || (result.items && result.items[0] && result.items[0].product_title) || '';
      if (B.cartType === 'drawer' && overlays.BoloCartDrawer) { overlays.BoloCartDrawer.open(); }
      else { toast(B.strings.itemAdded.replace('[title]', title), 'success'); }
      document.dispatchEvent(new CustomEvent('bolo:cart:added', { detail: result }));
    }
  };
  window.BoloCart = Cart;

  /* ---------- quick add ---------------------------------------------------- */
  function bindQuickAddForm(form) {
    if (form.dataset.bound) return; form.dataset.bound = '1';
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var btn = $('[data-quick-add-button], [type=submit]', form); var id = $('[name=id]', form).value;
      if (btn) btn.classList.add('is-loading');
      Cart.add({ items: [{ id: parseInt(id, 10), quantity: 1 }] }).then(function (res) { Cart.added(res); closeQuickAdd(); })
        .catch(function (err) { toast((err && err.description) || B.strings.error, 'error'); })
        .then(function () { if (btn) btn.classList.remove('is-loading'); });
    });
  }
  var qaRelease = null;
  function bindQuickAddOpen(btn) {
    if (btn.dataset.bound) return; btn.dataset.bound = '1';
    btn.addEventListener('click', function () {
      var host = $('#BoloQuickAdd'); var content = $('[data-quick-add-content]', host);
      btn.classList.add('is-loading');
      fetch(btn.dataset.productUrl + '?section_id=quick-add').then(function (r) { return r.text(); }).then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var inner = doc.querySelector('[data-quick-add-inner]');
        content.innerHTML = inner ? inner.innerHTML : '';
        host.hidden = false; lockScroll();
        qaRelease = trapFocus(host.querySelector('[role=dialog]'));
        $$('[data-product-form]', content).forEach(initProductForm);
      }).catch(function () { toast(B.strings.error, 'error'); }).then(function () { btn.classList.remove('is-loading'); });
    });
  }
  function closeQuickAdd() {
    var host = $('#BoloQuickAdd'); if (!host || host.hidden) return;
    host.hidden = true; unlockScroll(); if (qaRelease) { qaRelease(); qaRelease = null; }
  }
  document.addEventListener('click', function (e) { if (e.target.closest('[data-quick-add-close]')) closeQuickAdd(); });

  /* ---------- product form (PDP + quick add) ------------------------------- */
  function initProductForm(form) {
    if (form.dataset.bound) return; form.dataset.bound = '1';
    var variantsEl = $('[data-variants-json]', form);
    if (!variantsEl) return;
    var variants = JSON.parse(variantsEl.textContent);
    var product = form.dataset.productHandle;
    var isPdp = form.hasAttribute('data-pdp');
    var idInput = $('[name=id]', form);
    var atc = $('[data-atc]', form);
    var atcLabel = $('[data-atc-label]', form);
    var scope = form.closest('[data-quick-add-inner], .bolo-pdp');
    var priceHost = $('[data-price]', form) || (scope ? $('[data-price]', scope) : null);
    var errorEl = $('[data-form-error]', form);
    var stockEl = isPdp ? $('[data-stock-note]') : null;
    var lowThreshold = parseInt(form.dataset.lowStock || '0', 10);

    function selectedOptions() {
      var opts = [];
      $$('[data-option-index]', form).forEach(function (group) {
        var checked = $('input:checked', group) || $('select', group);
        opts[parseInt(group.dataset.optionIndex, 10)] = checked ? checked.value : null;
      });
      return opts;
    }
    function findVariant(opts) {
      return variants.find(function (v) { return v.options.every(function (o, i) { return o === opts[i]; }); }) || null;
    }
    function updateAvailabilityStates(opts) {
      // Mark option chips unavailable when no available variant matches the *other* chosen options.
      $$('[data-option-index]', form).forEach(function (group) {
        var idx = parseInt(group.dataset.optionIndex, 10);
        $$('input', group).forEach(function (input) {
          var test = opts.slice(); test[idx] = input.value;
          var any = variants.some(function (v) { return v.available && v.options.every(function (o, i) { return i === idx ? o === input.value : (opts[i] == null || o === opts[i]); }); });
          var chip = input.nextElementSibling; if (chip) chip.classList.toggle('bolo-chip--unavailable', !any);
        });
      });
    }
    function render() {
      var opts = selectedOptions();
      var v = findVariant(opts);
      updateAvailabilityStates(opts);
      $$('[data-option-index]', form).forEach(function (g) { var val = $('[data-option-value]', g); var checked = $('input:checked', g); if (val && checked) val.textContent = checked.value; });
      if (!v) {
        if (atc) { atc.disabled = true; if (atcLabel) atcLabel.textContent = B.strings.unavailable; }
        return;
      }
      idInput.value = v.id;
      if (atc) {
        atc.disabled = !v.available;
        if (atcLabel) atcLabel.textContent = v.available ? B.strings.addToCart : B.strings.soldOut;
      }
      if (priceHost) {
        var html = '';
        if (v.compare_at_price && v.compare_at_price > v.price) html += '<s class="bolo-price__compare">' + formatMoney(v.compare_at_price) + '</s> ';
        html += '<span class="bolo-price__amount">' + formatMoney(v.price) + '</span>';
        if (v.compare_at_price && v.compare_at_price > v.price) html += ' <span class="bolo-tag">' + (priceHost.dataset.saleLabel || 'Sale') + '</span>';
        priceHost.innerHTML = html;
      }
      if (isPdp) {
        // URL + sticky bar + stock note + gallery image jump
        if (window.history.replaceState) { var u = new URL(window.location.href); u.searchParams.set('variant', v.id); window.history.replaceState({}, '', u.toString()); }
        var sticky = $('[data-sticky-atc]');
        if (sticky) { var sp = $('[data-sticky-price]', sticky); if (sp) sp.textContent = formatMoney(v.price); var sb = $('[data-sticky-button]', sticky); if (sb) { sb.disabled = !v.available; sb.textContent = v.available ? B.strings.addToCart : B.strings.soldOut; } }
        if (stockEl) {
          stockEl.className = 'bolo-pdp__stock';
          if (!v.available) { stockEl.textContent = stockEl.dataset.out; stockEl.classList.add('bolo-pdp__stock--out'); }
          else if (v.inventory_management && v.inventory_quantity > 0 && lowThreshold && v.inventory_quantity <= lowThreshold) { stockEl.textContent = stockEl.dataset.low.replace('[count]', v.inventory_quantity); stockEl.classList.add('bolo-pdp__stock--low'); }
          else { stockEl.textContent = stockEl.dataset.in; stockEl.classList.add('bolo-pdp__stock--in'); }
        }
        if (v.featured_media && v.featured_media.id) {
          var target = $('[data-media-id="' + v.featured_media.id + '"]');
          if (target) { var gallery = target.closest('[data-gallery]'); if (gallery && window.innerWidth < 990) { gallery.scrollTo({ left: target.offsetLeft - gallery.offsetLeft, behavior: reduceMotion ? 'auto' : 'smooth' }); } else if (target.scrollIntoView && window.innerWidth >= 990) { target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'nearest' }); } }
        }
      }
    }
    form.addEventListener('change', function (e) { if (e.target.closest('[data-option-index]')) render(); });
    render();

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      if (errorEl) errorEl.textContent = '';
      var qtyEl = $('[name=quantity]', form);
      var qty = qtyEl ? parseInt(qtyEl.value, 10) || 1 : 1;
      var id = parseInt(idInput.value, 10);
      if (!id) { if (errorEl) errorEl.textContent = B.strings.unavailable; return; }
      if (atc) atc.classList.add('is-loading');
      Cart.add({ items: [{ id: id, quantity: qty }] }).then(function (res) { Cart.added(res); closeQuickAdd(); })
        .catch(function (err) { var msg = (err && (err.description || err.message)) || B.strings.error; if (errorEl) errorEl.textContent = msg; else toast(msg, 'error'); })
        .then(function () { if (atc) atc.classList.remove('is-loading'); });
    });
    // Sticky bar submits the main form
    var stickyBtn = $('[data-sticky-button]');
    if (isPdp && stickyBtn) stickyBtn.addEventListener('click', function () { form.requestSubmit ? form.requestSubmit() : form.dispatchEvent(new Event('submit', { cancelable: true })); });
  }

  /* ---------- quantity steppers (non-cart) -------------------------------- */
  function initQty() {
    $$('[data-qty]').forEach(function (wrap) {
      var input = $('input', wrap);
      $$('[data-qty-minus],[data-qty-plus]', wrap).forEach(function (btn) {
        btn.addEventListener('click', function () { var v = parseInt(input.value, 10) || 1; v += btn.hasAttribute('data-qty-plus') ? 1 : -1; input.value = Math.max(parseInt(input.min || '1', 10), v); input.dispatchEvent(new Event('change', { bubbles: true })); });
      });
    });
  }

  /* ---------- PDP gallery -------------------------------------------------- */
  function initGallery() {
    var gallery = $('[data-gallery]'); if (!gallery) return;
    var items = $$('.bolo-gallery__item', gallery);
    var dots = $$('[data-gallery-dot]');
    var thumbs = $$('[data-gallery-thumb]');
    function setActive(i) { dots.forEach(function (d, j) { d.classList.toggle('is-active', i === j); }); thumbs.forEach(function (t, j) { t.classList.toggle('is-active', i === j); }); }
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) { entries.forEach(function (en) { if (en.isIntersecting && en.intersectionRatio > 0.55) setActive(items.indexOf(en.target)); }); }, { root: window.innerWidth < 990 ? gallery : null, threshold: [0.6] });
      items.forEach(function (it) { io.observe(it); });
    }
    thumbs.forEach(function (t, i) { t.addEventListener('click', function () { items[i].scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'nearest' }); setActive(i); }); });
    dots.forEach(function (d, i) { d.addEventListener('click', function () { gallery.scrollTo({ left: items[i].offsetLeft - gallery.offsetLeft, behavior: reduceMotion ? 'auto' : 'smooth' }); }); });
    // Zoom
    $$('[data-zoom]', gallery).forEach(function (btn) {
      btn.addEventListener('click', function () {
        var src = btn.dataset.zoom; if (!src) return;
        var z = document.createElement('div'); z.className = 'bolo-zoom'; z.setAttribute('role', 'dialog'); z.setAttribute('aria-modal', 'true'); z.setAttribute('aria-label', btn.getAttribute('aria-label') || 'Zoom');
        z.innerHTML = '<button type="button" class="bolo-icon-btn bolo-zoom__close" aria-label="Close"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M6 6l12 12M18 6 6 18"/></svg></button><img src="' + src + '" alt="">';
        document.body.appendChild(z); lockScroll();
        var rel = trapFocus(z);
        var close = function () { z.remove(); unlockScroll(); rel(); };
        z.addEventListener('click', function (e) { if (e.target.tagName === 'IMG' || e.target.closest('.bolo-zoom__close')) close(); });
      });
    });
  }

  /* ---------- sticky add-to-cart ------------------------------------------ */
  function initStickyAtc() {
    var bar = $('[data-sticky-atc]'); var anchor = $('[data-atc]'); if (!bar || !anchor) return;
    if (!('IntersectionObserver' in window)) return;
    var io = new IntersectionObserver(function (entries) { entries.forEach(function (en) { var mobile = window.innerWidth < 990; bar.classList.toggle('is-visible', !en.isIntersecting && (mobile || en.boundingClientRect.top < 0)); }); }, { threshold: 0 });
    io.observe(anchor);
  }

  /* ---------- accordions --------------------------------------------------- */
  function initAccordions() {
    $$('[data-accordion-trigger]').forEach(function (btn) {
      var panel = document.getElementById(btn.getAttribute('aria-controls')); if (!panel) return;
      if (btn.getAttribute('aria-expanded') === 'true') panel.classList.add('is-open');
      btn.addEventListener('click', function () {
        var open = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', String(!open)); panel.classList.toggle('is-open', !open);
      });
    });
  }

  /* ---------- collection filters + sort ----------------------------------- */
  function initCollection() {
    var grid = $('[data-collection-grid]'); if (!grid) return;
    var pills = $$('[data-filter]'); var cards = $$('[data-filter-key]', grid); var count = $('[data-collection-count]'); var empty = $('[data-collection-empty]');
    function apply(key) {
      var visible = 0;
      cards.forEach(function (c) { var keys = (c.dataset.filterKey || '').toLowerCase().split(' '); var show = key === 'all' || keys.indexOf(key) > -1; if (show) { c.removeAttribute('data-filter-hidden'); visible++; } else c.setAttribute('data-filter-hidden', ''); });
      pills.forEach(function (p) { p.setAttribute('aria-pressed', String(p.dataset.filter === key)); });
      if (count) count.textContent = count.dataset.template.replace('[count]', visible);
      if (empty) empty.hidden = visible > 0;
      var u = new URL(window.location.href); if (key === 'all') u.searchParams.delete('type'); else u.searchParams.set('type', key); window.history.replaceState({}, '', u.toString());
    }
    pills.forEach(function (p) { p.addEventListener('click', function () { apply(p.dataset.filter); }); });
    var sp = new URL(window.location.href).searchParams; var initial = sp.get('filter') || sp.get('type');
    if (initial && pills.some(function (p) { return p.dataset.filter === initial; })) apply(initial);
    var sort = $('[data-sort]');
    if (sort) sort.addEventListener('change', function () { var u = new URL(window.location.href); u.searchParams.set('sort_by', sort.value); window.location.href = u.toString(); });
  }

  /* ---------- promo popup -------------------------------------------------- */
  function initPopup() {
    var cfg = B.popup || {}; var api = registerOverlay('BoloPopup');
    if (!api || !cfg.enabled) return;
    var key = 'bolo:popup:dismissed';
    var until = storage.get(key);
    if (until && Date.now() < until) return;
    if (storage.get('bolo:popup:subscribed')) return;
    var shown = false;
    function show() { if (shown) return; shown = true; if ($('#BoloCartDrawer.is-open') || $('#BoloMenu.is-open')) { setTimeout(show, 4000); shown = false; return; } api.open(); }
    var dismiss = function () { storage.set(key, Date.now() + (cfg.suppressDays || 14) * 864e5); };
    api.root.addEventListener('click', function (e) { if (e.target.closest('[data-overlay-close], [data-popup-decline]')) dismiss(); });
    var origClose = api.close; api.close = function () { dismiss(); origClose(); };
    setTimeout(show, (cfg.delay || 6) * 1000);
    if (cfg.exitIntent && window.matchMedia('(pointer:fine)').matches) {
      document.addEventListener('mouseout', function (e) { if (!e.relatedTarget && e.clientY <= 0) show(); });
    }
  }

  /* ---------- newsletter forms (AJAX to Shopify customer form) ------------ */
  function initNewsletter() {
    $$('[data-newsletter-form]').forEach(function (wrap) {
      var form = wrap.tagName === 'FORM' ? wrap : $('form', wrap); if (!form) return;
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var status = $('[data-newsletter-status]', form); var btn = $('[type=submit]', form); var email = $('[type=email]', form);
        if (!email || !email.checkValidity()) { if (status) { status.textContent = B.strings.newsletterError; status.className = 'bolo-form-status is-error'; } return; }
        if (btn) btn.classList.add('is-loading');
        fetch(form.action, { method: 'POST', body: new FormData(form), headers: { 'X-Requested-With': 'XMLHttpRequest' } })
          .then(function (r) {
            if (status) { status.textContent = form.dataset.success || B.strings.newsletterSuccess; status.className = 'bolo-form-status is-success'; }
            email.value = '';
            var inPopup = form.closest('#BoloPopup');
            if (inPopup) { storage.set('bolo:popup:subscribed', true); var note = $('[data-popup-note]', inPopup); if (note) note.hidden = false; var fields = $('[data-popup-fields]', inPopup); if (fields) fields.hidden = true; }
            else toast(B.strings.newsletterSuccess, 'success');
          })
          .catch(function () { if (status) { status.textContent = B.strings.error; status.className = 'bolo-form-status is-error'; } })
          .then(function () { if (btn) btn.classList.remove('is-loading'); });
      });
    });
  }

  /* ---------- recently viewed --------------------------------------------- */
  function initRecentlyViewed() {
    var key = 'bolo:recent';
    var current = $('[data-product-handle-current]');
    var list = storage.get(key) || [];
    if (current) {
      var h = current.dataset.productHandleCurrent;
      list = [h].concat(list.filter(function (x) { return x !== h; })).slice(0, 8);
      storage.set(key, list);
    }
    var host = $('[data-recently-viewed]'); if (!host) return;
    var handles = list.filter(function (x) { return !current || x !== current.dataset.productHandleCurrent; }).slice(0, 4);
    if (!handles.length) return;
    var q = handles.map(function (h) { return 'handle:' + h; }).join(' OR ');
    fetch(B.routes.search + '?type=product&q=' + encodeURIComponent(q) + '&section_id=recently-viewed').then(function (r) { return r.text(); }).then(function (html) {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var inner = doc.querySelector('[data-recently-inner]');
      if (inner && inner.querySelector('[data-product-card]')) { host.innerHTML = inner.innerHTML; host.hidden = false; Cart.bind(host); }
    }).catch(function () {});
  }

  /* ---------- parallax media & scroll-driven lookbook ---------------------- */
  function initScrollFx() {
    if (reduceMotion) return;
    var px = $$('[data-parallax]');
    var lb = $('[data-lookbook].bolo-lookbook--drive');
    var track = lb ? $('[data-lookbook-track]', lb) : null;
    var vh = window.innerHeight, vw = window.innerWidth, maxX = 0, lbTop = 0, lbRange = 1, ticking = false;
    function measure() {
      vh = window.innerHeight; vw = window.innerWidth;
      if (lb && track && vw >= 990) {
        var gutter = parseFloat(getComputedStyle(track).paddingLeft) || 0;
        maxX = Math.max(0, track.scrollWidth - vw + gutter);
        var h = vh + maxX;
        lb.style.setProperty('--lb-height', h + 'px');
        var r = lb.getBoundingClientRect();
        lbTop = r.top + window.scrollY; lbRange = Math.max(1, h - vh);
      } else if (lb) { lb.style.removeProperty('--lb-height'); lb.style.removeProperty('--lb-x'); }
      update();
    }
    function update() {
      ticking = false;
      if (vw >= 768) {
        px.forEach(function (el) {
          var host = el.parentElement; var r = host.getBoundingClientRect();
          if (r.bottom < 0 || r.top > vh) return;
          var speed = parseFloat(el.dataset.parallaxSpeed) || .2;
          var progress = (r.top + r.height / 2 - vh / 2) / vh; // -1..1
          var limit = r.height * .12;
          var y = Math.max(-limit, Math.min(limit, -progress * speed * r.height));
          el.style.transform = 'translate3d(0,' + y.toFixed(1) + 'px,0)';
        });
      }
      if (lb && track && vw >= 990 && maxX > 0) {
        var p = Math.min(1, Math.max(0, (window.scrollY - lbTop) / lbRange));
        lb.style.setProperty('--lb-x', (-p * maxX).toFixed(1) + 'px');
      }
    }
    function onScroll() { if (!ticking) { ticking = true; requestAnimationFrame(update); } }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', debounce(measure, 150));
    window.addEventListener('load', measure);
    if (track && 'ResizeObserver' in window) new ResizeObserver(debounce(measure, 100)).observe(track);
    measure();
  }

  /* ---------- product recommendations (Complete the set) ------------------ */
  function initRecommendations() {
    var host = $('[data-recommendations]'); if (!host || !host.dataset.url) return;
    fetch(host.dataset.url).then(function (r) { return r.text(); }).then(function (html) {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var fresh = doc.querySelector('[data-recommendations]');
      if (fresh && fresh.querySelector('[data-product-card]')) { host.innerHTML = fresh.innerHTML; Cart.bind(host); }
    }).catch(function () {});
  }

  /* ---------- misc: share, localization, cart page, contact ---------------- */
  function initMisc() {
    $$('[data-copy-link]').forEach(function (btn) {
      btn.addEventListener('click', function () { var url = btn.dataset.copyLink || window.location.href; if (navigator.clipboard) navigator.clipboard.writeText(url).then(function () { toast(B.strings.linkCopied, 'success'); }); });
    });
    $$('[data-localization-form] select').forEach(function (sel) { sel.addEventListener('change', function () { sel.form.submit(); }); });
    $$('[data-cart-count-source]').forEach(function (el) { Cart.setCount(el.dataset.cartCountSource); });
    // Reviews slider controls (mobile)
    $$('[data-slider]').forEach(function (s) {
      var track = $('[data-slider-track]', s); if (!track) return;
      $$('[data-slider-prev],[data-slider-next]', s).forEach(function (b) { b.addEventListener('click', function () { var w = track.firstElementChild ? track.firstElementChild.getBoundingClientRect().width + 16 : 300; track.scrollBy({ left: b.hasAttribute('data-slider-next') ? w : -w, behavior: reduceMotion ? 'auto' : 'smooth' }); }); });
    });
    // Address forms (customers/addresses)
    $$('[data-address-toggle]').forEach(function (btn) { btn.addEventListener('click', function () { var t = document.getElementById(btn.getAttribute('aria-controls')); if (!t) return; var open = !t.hidden; t.hidden = open; btn.setAttribute('aria-expanded', String(!open)); if (!open) { var f = t.querySelector('input'); if (f) f.focus(); } }); });
    $$('[data-address-delete]').forEach(function (btn) { btn.addEventListener('click', function () { if (window.confirm(btn.dataset.confirm)) { var f = document.createElement('form'); f.method = 'post'; f.action = btn.dataset.target; f.innerHTML = '<input type="hidden" name="_method" value="delete">'; document.body.appendChild(f); f.submit(); } }); });
    if (window.Shopify && window.Shopify.CountryProvinceSelector) {
      $$('[data-address-country]').forEach(function (c) { new window.Shopify.CountryProvinceSelector(c.id, c.dataset.provinceTarget, { hideElement: c.dataset.provinceWrap }); });
    }
    // Password page toggle
    var pwToggle = $('[data-password-toggle]'); if (pwToggle) pwToggle.addEventListener('click', function () { var t = $('[data-password-form]'); if (t) { t.hidden = !t.hidden; if (!t.hidden) { var i = t.querySelector('input[type=password]'); if (i) i.focus(); } } });
  }

  /* ---------- boot --------------------------------------------------------- */
  function boot() {
    initReveal(); initHeader(); initAnnouncement(); initOverlays(); initPredictiveSearch();
    Cart.bind(document); initQty(); $$('[data-product-form]').forEach(initProductForm);
    initGallery(); initStickyAtc(); initAccordions(); initCollection(); initPopup(); initNewsletter(); initRecentlyViewed(); initRecommendations(); initScrollFx(); initMisc();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();

  // Theme editor: re-bind when sections reload
  document.addEventListener('shopify:section:load', function () { Cart.bind(document); initAccordions(); initReveal(); $$('[data-product-form]').forEach(initProductForm); });
})();
